import { useCallback, useEffect, useId, useRef, useState } from 'react';
import type { CSSProperties, KeyboardEvent, PointerEvent } from 'react';
import { createFrameCoalescer } from './frame_coalescer';
import { useReorderAnnouncement } from './use_reorder_announcement';

type DragState = {
   /** Index of the dragged item in the source list. */
   from: number;
   /** Where the dragged item currently sits in the display list. */
   over: number;
   /** Pointer that started the drag; others are ignored while it runs. */
   pointerId: number;
};

/** Marks an element as a hover target; rows are found by hit-test. */
export type ReorderRowProps = {
   'data-reorder-index': number;
   'data-reorder-group': string;
};

/** Pixels from the viewport edge where dragging nudges the page along. */
const SCROLL_MARGIN = 90;
const SCROLL_STEP = 12;
/** Pointer travel before a press on a row body becomes a drag, not a click. */
const DRAG_THRESHOLD = 5;

/**
 * List reordering with live preview: while a drag is in flight the list
 * renders with the dragged item moved to the hovered position, and the
 * reorder commits once on release.
 *
 * Built on Pointer Events so mouse and touch share one path (HTML5 drag
 * events never fire on touch). A drag starts on the handle — which gets
 * `touch-action: none` so the browser doesn't claim the gesture for
 * scrolling — then follows the pointer through window-level listeners.
 * Not pointer capture: every preview swap makes React re-insert the
 * dragged row's DOM node, and Chrome silently releases capture when a
 * captured element moves, leaving a capturing handle deaf mid-drag (the
 * row froze until the pointer wandered back over the grip). The window
 * hears the pointer no matter which element the events target.
 *
 * Render rows from `displayItems(items)` — during a drag it returns the
 * previewed order; each entry carries its `sourceIndex` so row controls
 * (edit, remove) keep addressing the real list. Spread `handleProps` on
 * the grab handle and `rowProps` on every row (the hover targets).
 *
 * Pass `rowProps(i, { draggable: true })` to also start a drag from a press
 * anywhere on the row (steps, prerequisites) — it waits for a few pixels of
 * travel so a plain click still activates the row, and leaves the row's
 * `touch-action` alone so touch still scrolls the list. The grip stays the
 * touch and keyboard affordance (its `touch-action: none` is what lets a touch
 * drag from it). Presses on controls (menus, links, remove) and text fields
 * are their own click/selection, not a drag; a control that IS the row body
 * (a step's select button) marks itself `data-drag` to grab from it too.
 */
export function useDragReorder(onReorder: (from: number, to: number) => void) {
   // Distinguishes this list's rows from any other reorder list mounted at
   // the same time (tools/parts/prereqs share a tab; the step rail and media
   // tiles share the Steps tab), so a drag hit-tests only its own rows.
   const groupId = useId();
   // State drives the preview render; the ref keeps the event handlers
   // honest (pointerup closures would otherwise see a stale snapshot).
   const [dragState, setDragState] = useState<DragState | null>(null);
   const dragRef = useRef<DragState | null>(null);
   // Screen-reader feedback for keyboard reordering; the caller renders it
   // into an aria-live region so a move made with the arrow keys is spoken.
   const { announcement, announce } = useReorderAnnouncement();
   // The reorder callback lives in a ref so handleProps/rowProps below stay
   // referentially stable across renders — long lists (the step rail) can
   // then memoize their rows, and a drag re-renders only the rows whose
   // preview position actually changed instead of the whole list.
   const onReorderRef = useRef(onReorder);
   useEffect(() => {
      onReorderRef.current = onReorder;
   });
   const update = useCallback((next: DragState | null) => {
      dragRef.current = next;
      setDragState(next);
   }, []);

   // Tears down the active drag's window listeners; on drop, cancel, or
   // unmount mid-drag.
   const detachRef = useRef<(() => void) | null>(null);
   useEffect(() => () => detachRef.current?.(), []);

   // The shared pointer-drag engine. A dedicated grip drags immediately
   // (`threshold: false`); a whole-row press waits for DRAG_THRESHOLD of travel
   // so a plain click still selects or activates the row.
   const beginDrag = useCallback(
      (displayIndex: number, event: PointerEvent<HTMLElement>, threshold: boolean) => {
         if (!event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0)) {
            return;
         }
         const target = event.target as HTMLElement;
         if (threshold) {
            // Grab anywhere on the row, but a press on a text field is a
            // caret/selection, and a press on a control (menu, link, remove)
            // is that control's click — not a drag. A control that IS the row's
            // body (a step's select button) opts back in with data-drag.
            if (target.closest('input, textarea, select, [contenteditable="true"]')) {
               return;
            }
            const control = target.closest('button, a');
            if (control && !control.closest('[data-drag]')) {
               return;
            }
         } else {
            // A dedicated grip: a press on a nested control is that control's
            // click, and it must not also bubble up to a whole-row drag.
            if (target.closest('button, a, input, select, textarea')) {
               return;
            }
            event.stopPropagation();
         }

         // Scroll nudging finds the list's scroll container from an element
         // inside the list; the anchor serves even after React moves the
         // dragged node during preview swaps.
         const anchor = event.currentTarget;
         const { pointerId } = event;
         const startX = event.clientX;
         const startY = event.clientY;
         let dragging = false;
         let moved = false;

         const begin = () => {
            dragging = true;
            // A grip's pointerdown preventDefault kills text selection up front;
            // a row press can't (it might still be a click), so guard it here.
            document.body.style.userSelect = 'none';
            // A drag can only start from an unpreviewed list, where display
            // order and source order agree.
            update({ from: displayIndex, over: displayIndex, pointerId });
         };

         detachRef.current?.();
         if (!threshold) {
            // No text selection or focus theft while dragging from a grip.
            event.preventDefault();
            begin();
         }

         // pointermove can fire faster than the screen repaints; nudgeScroll
         // and the elementFromPoint hit-test both force layout, so doing them
         // on every raw event makes a fast drag janky. Buffer the latest
         // point and do one measure/hit-test pass per animation frame
         // instead — the preview still tracks the pointer, just no more
         // often than the screen can show it.
         const pointCoalescer = createFrameCoalescer(
            (point: { clientX: number; clientY: number }) => {
               const { clientX, clientY } = point;
               // Rows beyond the visible area are unreachable without this nudge.
               nudgeScroll(clientY, anchor);
               const drag = dragRef.current;
               if (!drag) {
                  return;
               }
               const over = displayIndexUnderPoint(clientX, clientY, groupId);
               if (over !== null && over !== drag.over) {
                  update({ ...drag, over });
               }
            }
         );

         const move = (moveEvent: globalThis.PointerEvent) => {
            if (moveEvent.pointerId !== pointerId) {
               return;
            }
            if (!dragging) {
               const travel = Math.hypot(moveEvent.clientX - startX, moveEvent.clientY - startY);
               if (travel < DRAG_THRESHOLD) {
                  return;
               }
               begin();
            }
            moved = true;
            pointCoalescer.send({ clientX: moveEvent.clientX, clientY: moveEvent.clientY });
         };
         const finish = (endEvent: globalThis.PointerEvent, commit: boolean) => {
            if (endEvent.pointerId !== pointerId) {
               return;
            }
            // The pointer already stopped; apply its last position before
            // reading dragRef so a release inside the same frame as the last
            // move still commits where the preview actually landed.
            pointCoalescer.flush();
            detach();
            const drag = dragRef.current;
            update(null);
            if (commit && drag && drag.over !== drag.from) {
               onReorderRef.current(drag.from, drag.over);
            }
            // A row-body drag would otherwise fire a click on release (selecting
            // the row it just moved); swallow that one click.
            if (threshold && moved) {
               suppressNextClick();
            }
         };
         const drop = (endEvent: globalThis.PointerEvent) => finish(endEvent, true);
         // Browser took the pointer back (e.g. an OS gesture): snap back, don't
         // commit.
         const cancel = (endEvent: globalThis.PointerEvent) => finish(endEvent, false);
         const detach = () => {
            pointCoalescer.cancel();
            detachRef.current = null;
            document.body.style.userSelect = '';
            window.removeEventListener('pointermove', move);
            window.removeEventListener('pointerup', drop);
            window.removeEventListener('pointercancel', cancel);
         };
         window.addEventListener('pointermove', move);
         window.addEventListener('pointerup', drop);
         window.addEventListener('pointercancel', cancel);
         detachRef.current = detach;
      },
      [groupId, update]
   );

   const handleProps = useCallback(
      (displayIndex: number, keyboard?: { label: string }) => {
         const base = {
            style: { touchAction: 'none' } as CSSProperties,
            onPointerDown: (event: PointerEvent<HTMLElement>) =>
               beginDrag(displayIndex, event, false),
         };
         // Drag-only handles (tools, parts) get the pointer behavior alone.
         // Pass a label to also make the handle a keyboard-reorderable button.
         if (!keyboard) {
            return base;
         }
         return {
            ...base,
            tabIndex: 0,
            role: 'button' as const,
            'aria-label': keyboard.label,
            onKeyDown: (event: KeyboardEvent<HTMLElement>) =>
               keyboardReorder(event, displayIndex, groupId, onReorderRef.current, announce),
         };
      },
      [beginDrag, groupId]
   );

   const rowProps = useCallback(
      (
         displayIndex: number,
         options?: { draggable?: boolean }
      ): ReorderRowProps & { onPointerDown?: (event: PointerEvent<HTMLElement>) => void } => ({
         'data-reorder-index': displayIndex,
         'data-reorder-group': groupId,
         // Opt in to grabbing anywhere on the row (steps, prerequisites); the
         // grip stays as the touch and keyboard affordance. Plain lists (tools,
         // parts) leave this off and drag from the grip alone.
         ...(options?.draggable && {
            onPointerDown: (event: PointerEvent<HTMLElement>) =>
               beginDrag(displayIndex, event, true),
         }),
      }),
      [groupId, beginDrag]
   );

   /** The list in its current display order, with source indexes attached. */
   const displayItems = <Item>(items: Item[]): { item: Item; sourceIndex: number }[] => {
      const plain = () => items.map((item, index) => ({ item, sourceIndex: index }));
      // A refetch can shrink the list mid-drag (another session deleted a
      // row). A now-out-of-range from/over would make movedItem splice in
      // `undefined`; fall back to the unpreviewed order until the drag ends.
      if (dragState === null || dragState.from >= items.length || dragState.over >= items.length) {
         return plain();
      }
      const order = movedItem(
         items.map((_, index) => index),
         dragState.from,
         dragState.over
      );
      return order.map(sourceIndex => ({ item: items[sourceIndex], sourceIndex }));
   };

   return {
      handleProps,
      rowProps,
      displayItems,
      /** Live-region text for the last keyboard move; render it sr-only. */
      announcement,
      /** Display index of the item being dragged, for styling; null idle. */
      draggingIndex: dragState?.over ?? null,
   };
}

/**
 * Swallow the single click the browser synthesizes after a row-body drag, so a
 * reorder started on a clickable row (a step's select button) doesn't also fire
 * that click and select the row. Capture-phase so it beats React's root
 * listener; a microtask cleanup disarms it if no click follows (the drop landed
 * off any clickable).
 */
function suppressNextClick(): void {
   const swallow = (event: MouseEvent) => {
      event.stopPropagation();
      event.preventDefault();
      window.removeEventListener('click', swallow, { capture: true });
   };
   window.addEventListener('click', swallow, { capture: true });
   window.setTimeout(() => window.removeEventListener('click', swallow, { capture: true }), 0);
}

/**
 * Keyboard reordering for a focused grab handle: Arrow keys nudge the item one
 * slot (up/down or left/right), Home/End jump it to an end. Commits through the
 * same onReorder as a pointer drag and announces the new position. The row
 * count is read from the live DOM so it always matches the current list, even
 * after another session added or removed a row.
 */
function keyboardReorder(
   event: KeyboardEvent<HTMLElement>,
   from: number,
   groupId: string,
   onReorder: (from: number, to: number) => void,
   announce: (position: number, count: number) => void
): void {
   const count = document.querySelectorAll(`[data-reorder-group="${groupId}"]`).length;
   let to: number;
   switch (event.key) {
      case 'ArrowUp':
      case 'ArrowLeft': {
         to = from - 1;
         break;
      }
      case 'ArrowDown':
      case 'ArrowRight': {
         to = from + 1;
         break;
      }
      case 'Home': {
         to = 0;
         break;
      }
      case 'End': {
         to = count - 1;
         break;
      }
      default: {
         return;
      }
   }
   event.preventDefault();
   if (to < 0 || to >= count || to === from) {
      return;
   }
   onReorder(from, to);
   announce(to + 1, count);
}

/**
 * Scroll toward the pointer when it drags near an edge: the list's own
 * scroll container when it lives in one (the step rail), the page
 * otherwise. Without this, rows beyond the visible area are unreachable.
 * `anchor` is any element inside the list, used to find its scroll
 * container. Shared with the line editor's drag (use_line_drag.ts).
 */
export function nudgeScroll(clientY: number, anchor: HTMLElement): void {
   const scroller = nearestScroller(anchor);
   if (scroller) {
      const rect = scroller.getBoundingClientRect();
      // A short container gets a proportionally smaller hot zone so the
      // top and bottom zones can't overlap into always-scrolling.
      const margin = Math.min(SCROLL_MARGIN, rect.height / 4);
      if (clientY < rect.top + margin) {
         scroller.scrollTop -= SCROLL_STEP;
      } else if (clientY > rect.bottom - margin) {
         scroller.scrollTop += SCROLL_STEP;
      }
      return;
   }
   if (clientY < SCROLL_MARGIN) {
      window.scrollBy(0, -SCROLL_STEP);
   } else if (clientY > window.innerHeight - SCROLL_MARGIN) {
      window.scrollBy(0, SCROLL_STEP);
   }
}

function nearestScroller(element: HTMLElement): HTMLElement | null {
   for (let node = element.parentElement; node; node = node.parentElement) {
      if (node.scrollHeight > node.clientHeight) {
         const { overflowY } = getComputedStyle(node);
         if (overflowY === 'auto' || overflowY === 'scroll') {
            return node;
         }
      }
   }
   return null;
}

/**
 * The row under the pointer, found by hit-test: the drag listens at the
 * window, so the event target never identifies the hovered row itself.
 * Rows belonging to a different reorder list (different group) are
 * ignored, so dragging in one list can't drop into another.
 */
function displayIndexUnderPoint(clientX: number, clientY: number, groupId: string): number | null {
   const row = document
      .elementFromPoint(clientX, clientY)
      ?.closest<HTMLElement>('[data-reorder-index]');
   if (!row || row.dataset.reorderGroup !== groupId) {
      return null;
   }
   return Number(row.dataset.reorderIndex);
}

/** The list with the item at `from` moved to position `to`. */
export function movedItem<Item>(items: Item[], from: number, to: number): Item[] {
   const next = [...items];
   const [moved] = next.splice(from, 1);
   next.splice(to, 0, moved);
   return next;
}
